/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import debounce from '@spotahome/marketplace-common/src/utils/debounce';

import { Provider } from './utils/viewer360Context';
import SliderGallery from './SliderGallery';

import './FullScreenSlider.scss';

const getPanoramicHeight = width => Math.floor((width * 9) / 16);
const getPanoramicWidth = height => Math.floor((height * 16) / 9);

const isLandscapeLayout = () =>
  // eslint-disable-next-line no-restricted-globals
  screen.width / screen.height > 1.77 && screen.width < 1024;

const INITIAL_DIMENSIONS = {
  height: 'auto',
  width: 'auto'
};

const noOp = () => {};

const FullScreenSlider = ({
  tracks,
  defaultTrackToShow,
  handleClose = noOp,
  listingId,
  currentTrackItem
}) => {
  const [dimensions, setDimensions] = useState({
    ...INITIAL_DIMENSIONS,
    isLandscape: false,
    shouldUpdate: false
  });

  const containerRef = useRef(null);
  const counterRef = useRef(null);
  const carouselRef = useRef(null);

  const getContainerHeight = useCallback(() => {
    const container = containerRef.current;
    const height = container.clientHeight;
    const { paddingTop, paddingBottom } = getComputedStyle(container);

    return height - parseInt(paddingTop, 10) - parseInt(paddingBottom, 10);
  }, []);

  const getCounterHeight = useCallback(
    () => (counterRef.current ? counterRef.current.clientHeight : 0),
    []
  );

  const getCarouselWidth = useCallback(() => {
    const carousel = carouselRef.current;
    const width = carousel.clientWidth;
    const { marginLeft, marginRight } = getComputedStyle(carousel);
    return width + parseInt(marginLeft, 10) + parseInt(marginRight, 10);
  }, []);

  const getCarouselHeight = useCallback(() => {
    const carousel = carouselRef.current;
    const height = carousel.scrollHeight;
    const { paddingTop, paddingBottom } = getComputedStyle(carousel);
    return height + parseInt(paddingTop, 10) + parseInt(paddingBottom, 10);
  }, []);

  const updateDimensions = useCallback(() => {
    const newDimensions = {};
    const counterHeight = getCounterHeight();
    const containerHeight = getContainerHeight();

    let availableHeight = containerHeight;

    if (dimensions.isLandscape) {
      const containerWidth = containerRef.current.clientWidth;
      const availableWidth = containerWidth - getCarouselWidth();
      newDimensions.width = availableWidth;
      newDimensions.height = getPanoramicHeight(availableWidth) + counterHeight;
    } else {
      availableHeight -= getCarouselHeight();
      newDimensions.width = '100%';
      newDimensions.height =
        getPanoramicHeight(containerRef.current.clientWidth) + counterHeight;
    }

    if (newDimensions.height > availableHeight) {
      newDimensions.height = availableHeight;
      newDimensions.width = getPanoramicWidth(availableHeight - counterHeight);
    }

    setDimensions(prev => ({
      ...prev,
      ...INITIAL_DIMENSIONS,
      ...newDimensions,
      shouldUpdate: false
    }));
  }, [
    dimensions.isLandscape,
    getCounterHeight,
    getContainerHeight,
    getCarouselWidth,
    getCarouselHeight
  ]);

  const updateArrangement = useCallback(() => {
    const isLandscape = isLandscapeLayout();
    setDimensions(prev => ({ ...prev, isLandscape, shouldUpdate: true }));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rearrangeLayout = useCallback(
    debounce(() => {
      if (containerRef.current) {
        updateArrangement();
      }
    }),
    [updateArrangement]
  );

  useEffect(() => {
    window.addEventListener('resize', rearrangeLayout);
    updateArrangement();

    return () => {
      window.removeEventListener('resize', rearrangeLayout);
    };
  }, [rearrangeLayout, updateArrangement]);

  useEffect(() => {
    if (dimensions.shouldUpdate) {
      updateDimensions();
    }
  }, [dimensions.shouldUpdate, updateDimensions]);

  const handleFullScreenSliderClick = event => {
    if (event.target === containerRef.current) {
      handleClose();
    }
  };

  const handleRedimensionOnTrackChange = () => {
    updateDimensions();
  };

  const className = classNames('fullscreen-slider', {
    'fullscreen-slider--inline': dimensions.isLandscape
  });

  return (
    <div
      ref={containerRef}
      className={className}
      onClick={handleFullScreenSliderClick}
    >
      <Provider value={{ isLandscape: dimensions.isLandscape }}>
        <SliderGallery
          isLandscape={dimensions.isLandscape}
          tracks={tracks}
          height={dimensions.height}
          width={dimensions.width}
          counterRef={counterRef}
          carouselRef={carouselRef}
          defaultTrackToShow={defaultTrackToShow}
          onTrackChange={handleRedimensionOnTrackChange}
          currentTrackItem={currentTrackItem}
          listingId={listingId}
        />
      </Provider>
    </div>
  );
};

FullScreenSlider.propTypes = {
  tracks: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  defaultTrackToShow: PropTypes.number.isRequired,
  handleClose: PropTypes.func,
  listingId: PropTypes.string.isRequired,
  currentTrackItem: PropTypes.number.isRequired
};

export default FullScreenSlider;
